import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaxRuleToken, EnumToken, CountryToken, CountryRegionToken } from '@sleed/bizcommerce-api';
import { taxRulesApi, countriesApi, countryRegionsApi } from '~/lib/api/client';
import { formToToken, type TaxRuleFormValues } from '~/lib/schemas/tax-rule';

export const taxRuleKeys = {
	all: ['tax-rules'] as const,
	list: () => [...taxRuleKeys.all, 'list'] as const,
	taxStatuses: () => [...taxRuleKeys.all, 'tax-statuses'] as const,
	countries: () => [...taxRuleKeys.all, 'countries'] as const,
	regions: () => [...taxRuleKeys.all, 'regions'] as const
};

export function useTaxRulesList() {
	return useQuery<TaxRuleToken[]>({
		queryKey: taxRuleKeys.list(),
		queryFn: async () => (await taxRulesApi.getAll()).data.items ?? []
	});
}

export function useTaxRuleStatuses() {
	return useQuery<EnumToken[]>({
		queryKey: taxRuleKeys.taxStatuses(),
		queryFn: async () => (await taxRulesApi.getTaxStatuses()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxRuleCountries() {
	return useQuery<CountryToken[]>({
		queryKey: taxRuleKeys.countries(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxRuleRegions() {
	return useQuery<CountryRegionToken[]>({
		queryKey: taxRuleKeys.regions(),
		queryFn: async () => (await countryRegionsApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaxRuleMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: taxRuleKeys.list() });
	const create = useMutation({
		mutationFn: async (form: TaxRuleFormValues) => (await taxRulesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: TaxRuleFormValues) => (await taxRulesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await taxRulesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
